import * as React from "react";
import moment from "moment-timezone";

import type { Job } from "./ApplicationInvitations";

interface Application {
  id: number;
  created_at: string;
  updated_at: string;
  withdrawn: boolean;
  job: Job;
}

interface ApplicationProps {
  application: Application;
  authenticity_token: string;
}

export const ApplicationComponent: React.FC<ApplicationProps> = ({ application, authenticity_token }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { job } = application;

  // Format application date
  const applicationDate = React.useMemo(() => {
    if (!application.created_at || !job.practice.timezone) {
      return 'Date unavailable';
    }
    return moment.tz(application.created_at, job.practice.timezone).format("ddd, MMM D, YYYY h:mm A");
  }, [application.created_at, job.practice.timezone]);

  // Format withdrawn date if applicable
  const withdrawnDate = React.useMemo(() => {
    if (!application.withdrawn || !application.updated_at || !job.practice.timezone) {
      return '';
    }
    return moment.tz(application.updated_at, job.practice.timezone).format("ddd, MMM D, YYYY h:mm A");
  }, [application.withdrawn, application.updated_at, job.practice.timezone]);

  const handleWithdraw = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!confirm('Are you sure you want to withdraw this application?')) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`/job_applications/${application.id}/withdraw`, {
        method: "GET",
        headers: {
          "X-CSRF-Token": authenticity_token,
        },
      });

      if (response.ok) {
        // Reload the page to show updated status
        window.location.reload();
      } else {
        throw new Error('Failed to withdraw application');
      }
    } catch (error) {
      console.error("Error withdrawing application:", error);
      setError('Failed to withdraw application. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons or links
    if ((e.target as HTMLElement).closest('button, a')) {
      return;
    }
    window.location.href = `/jobs/${job.id}`;
  };

  const getBadgeColor = () => {
    if (application.withdrawn) {
      return 'bg-danger';
    }
    return 'bg-success';
  };

  const getStatusText = () => {
    if (application.withdrawn) {
      return 'Withdrawn';
    }
    return 'Applied';
  };

  return (
    <div
      className="field"
      style={{ cursor: 'pointer' }}
      onClick={handleClick}
    >
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          <i className="fa fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      <li className="col-12 d-flex justify-content-between border-top border-start border-end py-2 pt-1 pb-3 border-radius-1">
        <div className="col-2 p-3" style={{ minHeight: 40 }}>
          <img
            src={job.practice.avatar_url}
            alt={`${job.practice_name} logo`}
            className="img-fluid rounded-circle"
          />
        </div>

        <div className="col-10 d-flex flex-column justify-content-center">
          <div className="job-list-content">
            <div className="d-flex flex-row align-items-center pe-2">
              <h4 className="mb-0 mt-2 col-6 col-xl-8">
                {job.job_type.title}
              </h4>
              <div className="col-6 col-xl-4 text-end">
                <span className={`badge ${getBadgeColor()}`}>
                  {getStatusText()}
                </span>
              </div>
            </div>
            <div className="d-flex my-2 flex-wrap">
              <span className="profile-attribute-bubble rounded px-2 py-1 me-1">
                <i className="fa fa-briefcase"></i> {job.practice_name}
              </span>
              {job.practice.city && job.practice.state && (
                <span className="profile-attribute-bubble rounded px-2 py-1 me-1">
                  <i className="fa fa-map-marker"></i> {job.practice.city}, {job.practice.state}
                </span>
              )}
              {job.compensation_full && (
                <span className="profile-attribute-bubble rounded px-2 py-1 me-1">
                  <i className="fa fa-money"></i> {job.compensation_full}
                </span>
              )}
              <span className="profile-attribute-bubble rounded px-2 py-1 me-1">
                <i className="fa fa-calendar"></i> Applied {applicationDate}
              </span>
              {application.withdrawn && (
                <span className="profile-attribute-bubble rounded px-2 py-1 me-1 text-danger">
                  <i className="fa fa-times"></i> Withdrawn {withdrawnDate}
                </span>
              )}
            </div>

            {/* Action buttons row */}
            <div className="d-flex gap-2 mt-2">
              {!application.withdrawn && (
                <button
                  className="btn btn-light shadow"
                  onClick={handleWithdraw}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Withdrawing...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-times me-1"></i>
                      Withdraw Application
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </li>
      <div className="clearfix"></div>
    </div>
  );
};
